/**
 * Soul & memory — the tab (#42 §6, placed by #44 §3 on the persona
 * workspace).
 *
 * Variant A's two-column layout with variant B's hero lifted to the top:
 * what the agent learned most recently, then who it is (soul, left) and
 * what it has learned (memory, right).
 *
 * Four reads, all independent: a soul that cannot be read must not take
 * the memory panel down with it, so each half renders its own error state
 * rather than the tab going blank.
 */

import { useEffect, useState } from 'react';
import { SkeletonBodyText } from '@shopify/polaris';

import {
  getDecided,
  getMemory,
  getMemoryHero,
  getSoul,
  getSoulVersions,
} from '../../../services/agentMindApi';
import type {
  DecidedItem,
  MemoryHeroResponse,
  MemoryResponse,
  SoulResponse,
  SoulVersion,
} from '../../../types/agentMind';
import { WorkspaceEmpty } from '../WorkspaceEmpty';
import styles from '../workspace.module.css';
import { LearningHero } from './LearningHero';
import { MemoryPanel } from './MemoryPanel';
import { SoulPanel } from './SoulPanel';

function reason(err: unknown, fallback: string): string {
  return err instanceof Error ? err.message : fallback;
}

export function SoulMemoryTab({ domain }: { domain: string }) {
  const [soul, setSoul] = useState<SoulResponse | null>(null);
  const [versions, setVersions] = useState<SoulVersion[]>([]);
  const [hero, setHero] = useState<MemoryHeroResponse | null>(null);
  const [memory, setMemory] = useState<MemoryResponse | null>(null);
  const [decided, setDecided] = useState<DecidedItem[]>([]);
  const [soulError, setSoulError] = useState<string | null>(null);
  const [memoryError, setMemoryError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setSoulError(null);
    setMemoryError(null);

    Promise.allSettled([
      getSoul(domain),
      getSoulVersions(domain),
      getMemoryHero(domain),
      getMemory(domain, { limit: 25 }),
      getDecided(domain),
    ]).then((results) => {
      if (cancelled) return;
      const [soulResult, versionsResult, heroResult, memoryResult, decidedResult] =
        results;

      if (soulResult.status === 'fulfilled') setSoul(soulResult.value);
      else setSoulError(reason(soulResult.reason, 'Could not read the soul'));

      if (versionsResult.status === 'fulfilled') {
        setVersions(versionsResult.value.items ?? []);
      }
      if (heroResult.status === 'fulfilled') setHero(heroResult.value);

      if (memoryResult.status === 'fulfilled') setMemory(memoryResult.value);
      else {
        setMemoryError(
          reason(memoryResult.reason, 'Could not read the memory')
        );
      }
      if (decidedResult.status === 'fulfilled') {
        setDecided(decidedResult.value.items ?? []);
      }
      setLoading(false);
    });

    return () => {
      cancelled = true;
    };
  }, [domain]);

  return (
    <div data-testid="soul-memory-tab">
      {loading ? (
        <SkeletonBodyText lines={6} />
      ) : (
        <>
          {hero ? (
            <LearningHero hero={hero} />
          ) : (
            <WorkspaceEmpty
              kind="lane-error"
              reason="What this agent learned most recently could not be read."
            />
          )}

          <div className={styles.soulColumns}>
            <div>
              {soulError || !soul ? (
                <WorkspaceEmpty kind="lane-error" reason={soulError} />
              ) : (
                <SoulPanel soul={soul} versions={versions} />
              )}
            </div>
            <div>
              {memoryError || !memory ? (
                <WorkspaceEmpty kind="lane-error" reason={memoryError} />
              ) : (
                <MemoryPanel memory={memory} decided={decided} />
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
}
